import { useSignal } from '@preact/signals';
import { useCallback, useEffect, useMemo } from 'preact/hooks';
import type { TFunction, TOptions } from 'i18next';

import PriceContainer from '@nespresso/catalog-components-react/containers/PriceContainer';
import { Button } from '@nespresso-design-system/components';
import { type Subscription } from '@/domain/Subscription.ts';
import useTranslations from '@/widgets/hooks/useTranslations.tsx';
import styles from './SubscriptionOptionsDrawer.module.scss';
import SubscriptionItem from './SubscriptionItem/SubscriptionItem.tsx';
import { initialiseSubscriptionFlow } from '@/widgets/services/SubscriptionService.ts';
import type { Product } from '@/domain/Product.ts';
import useRoute from '@/widgets/hooks/useRoute.tsx';
import useMarketConfig from '@/widgets/hooks/useMarketConfig.tsx';
import { useCart } from '@/widgets/hooks/useCart.tsx';
import {
  trackAddToCartSubscription,
  trackSubscriptionsModalPlanList
} from '@/widgets/ProductDetailsHero/Tracking.ts';
import useWidgetOptions from '@/widgets/hooks/useWidgetOptions.tsx';
import type { Options } from '../../config/index.ts';
import AboutSubscription from './AboutSubscription/AboutSubscription.tsx';
import ModalOnDemand from '@/widgets/components/controls/ModalOnDemand/ModalOnDemand.tsx';
import usePriceFormatter from '@/widgets/hooks/usePriceFormatter.tsx';

export type SubscriptionOptionsDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  subscriptionsByDuration: { [duration: string]: Subscription[] };
  product: Product;
};

export default function SubscriptionOptionsDrawer({
  isOpen,
  onClose,
  subscriptionsByDuration,
  product
}: SubscriptionOptionsDrawerProps) {
  const formatPrice = usePriceFormatter();
  const { locale } = useRoute();
  const {
    financialSettings: { defaultCurrency: currencyCode },
    discoveryOfferBundleProducts
  } = useMarketConfig();

  const { hideSubscriptionDurations } = useWidgetOptions<Options>();

  const { t } = useTranslations();

  const isSavingSubscription = useSignal(false);
  const { cart, isReady, isLoading, addSubscription, removeSubscription } =
    useCart();

  // a list of the unique possible subscription terms available, based on the list of subscriptions
  const subscriptionDurations = useMemo(
    () => Object.keys(subscriptionsByDuration),
    []
  );
  const subscriptionEntries = Object.entries(subscriptionsByDuration);
  const isSingleDuration = subscriptionEntries.length === 1;

  const {
    selectedDuration,
    selectedSubscriptionId,
    setDuration,
    setSubscriptionId,
    reset
  } = useMemo(
    () => initialiseSubscriptionFlow(subscriptionsByDuration),
    [subscriptionsByDuration]
  );

  // on close clear selected duration
  useEffect(() => {
    if (!isOpen) {
      reset();
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedDuration.value && isOpen) {
      trackSubscriptionsModalPlanList(
        product,
        subscriptionsByDuration,
        selectedDuration.value,
        currencyCode
      );
    }
  }, [selectedDuration.value, subscriptionsByDuration, isOpen]);

  const subscriptionsFilteredByDuration = selectedDuration.value
    ? subscriptionsByDuration?.[selectedDuration.value]
    : [];

  const subscriptionsFlat = Object.values(subscriptionsByDuration).flat();
  const selectedSubscription = subscriptionsFlat.find(
    (subscription) => subscription.id === selectedSubscriptionId.value
  );

  const saveSubscription = useCallback(async () => {
    isSavingSubscription.value = true;
    await removeSubscription();
    await addSubscription(product.id, selectedSubscriptionId.value);
    if (selectedSubscription) {
      const selectedSubscriptionIndex =
        subscriptionsFilteredByDuration.indexOf(selectedSubscription);

      trackAddToCartSubscription(
        product,
        selectedSubscription,
        selectedSubscriptionIndex >= 0 ? selectedSubscriptionIndex + 1 : -1,
        currencyCode,
        discoveryOfferBundleProducts?.includes(product.id)
      );
    }
    isSavingSubscription.value = false;
    onClose();
  }, [selectedSubscriptionId.value, selectedSubscription, product?.id]);

  // profileId is the subscriptionId, not false or undefined
  const isSubscriptionInBasket =
    !!selectedSubscriptionId.value &&
    selectedSubscriptionId.value === cart.value?.subscription?.profileId &&
    product.id === cart.value?.subscription?.promotionalProductCode;
  const hasSubscription = !!cart.value?.subscription?.profileId;

  const subscriptionAddUpdateLabel = getSubscriptionLabel(
    t,
    isSubscriptionInBasket,
    hasSubscription
  );

  const price = product?.pricing?.price;

  if (!product && !isReady && !isLoading) return null;

  return (
    <ModalOnDemand
      heading={t('productDetailsHero.drawers.subscriptionOptionsDrawer.title')}
      position="side"
      isOpen={isOpen}
      onClose={onClose}
      closeBtnA11yLabel={t('components.modal.closeBtnA11yLabel')}
      fixedFooter
      footerSlot={
        <div class={styles.footer}>
          <Button
            onClick={saveSubscription}
            isLoading={isSavingSubscription.value}
            disabled={!product.inStock || isSubscriptionInBasket}
            label={
              product.inStock
                ? subscriptionAddUpdateLabel
                : t('components.addToCartButton.outOfStockLabel')
            }
          />
        </div>
      }
    >
      <>
        <div class={styles.subscriptionIntroduction}>
          <p>
            {t(
              'productDetailsHero.drawers.subscriptionOptionsDrawer.introduction'
            )}
          </p>
        </div>

        {/* Only show choose plan length buttons if there's more than 1 subscription duration available */}
        {!hideSubscriptionDurations && !isSingleDuration && (
          <div class={styles.subscriptionFilters}>
            <h3>
              {t(
                'productDetailsHero.drawers.subscriptionOptionsDrawer.monthsDuration'
              )}
            </h3>
            <p>
              {t(
                'productDetailsHero.drawers.subscriptionOptionsDrawer.planLengthDescription'
              )}
            </p>

            <div class={styles.subscriptionFiltersWrapper}>
              {subscriptionDurations.map((durationString) => {
                const duration = parseInt(durationString, 10);
                return (
                  <Button
                    variant={
                      selectedDuration.value !== duration
                        ? 'outline'
                        : 'primary'
                    }
                    onClick={() => {
                      setDuration(duration);
                    }}
                    label={t(
                      'productDetailsHero.drawers.subscriptionOptionsDrawer.filterDurationButton',
                      { duration }
                    )}
                  />
                );
              })}
            </div>
          </div>
        )}

        <fieldset class={styles.machineInfo}>
          {t(
            'productDetailsHero.drawers.subscriptionOptionsDrawer.legend.yourMachineLabel'
          ) ? (
            <legend>
              {t(
                'productDetailsHero.drawers.subscriptionOptionsDrawer.legend.yourMachineLabel'
              )}
            </legend>
          ) : (
            ''
          )}
          <img
            width="60"
            height="60"
            src={product.mainImage.url}
            alt={product.mainImage.alt}
          />

          <div>
            <h3>{product.name}</h3>

            {selectedSubscription ? (
              <PriceContainer
                price={selectedSubscription?.parameters.promotionalPrice}
                currencyCode={currencyCode}
                locale={locale}
                layoutMode="row"
                layoutOrder="price-first"
                strikethroughPrice={price}
                priceFormatter={(v) => formatPrice(v)}
              />
            ) : (
              <PriceContainer
                price={price}
                currencyCode={currencyCode}
                locale={locale}
                layoutMode="row"
                layoutOrder="price-first"
                priceFormatter={(v) => formatPrice(v)}
              />
            )}
            <p class={styles.oneTimePayment}>
              {t(
                'productDetailsHero.drawers.subscriptionOptionsDrawer.oneTimePayment'
              )}
            </p>
          </div>
        </fieldset>

        <div class={styles.subscriptionOptions}>
          {subscriptionsFilteredByDuration.length > 1 && (
            <div class={styles.choosePlanHeader}>
              <h3>
                {t(
                  'productDetailsHero.drawers.subscriptionOptionsDrawer.choosePlanSizeHeader'
                )}
              </h3>

              <p>
                {t(
                  'productDetailsHero.drawers.subscriptionOptionsDrawer.choosePlanSizeDescription'
                )}
              </p>
            </div>
          )}

          <div class={styles.subscriptionItems}>
            {subscriptionsFilteredByDuration.map((subscription) => (
              <SubscriptionItem
                key={subscription.id}
                productId={product.id}
                subscription={subscription}
                subscriptionsFilteredByDuration={
                  subscriptionsFilteredByDuration
                }
                selected={selectedSubscriptionId.value === subscription.id}
                onSubscriptionSelected={setSubscriptionId}
                duration={+selectedDuration.value}
                singleSubscription={
                  subscriptionsFilteredByDuration.length === 1
                }
              />
            ))}
          </div>
          <AboutSubscription
            product={product}
            selectedSubscription={selectedSubscription}
          />
        </div>
      </>
    </ModalOnDemand>
  );
}

function getSubscriptionLabel(
  t: TFunction<['translation', ...string[]], undefined> &
    ((key: never, options: TOptions) => string),
  isSubscriptionInBasket: boolean,
  hasSubscription: boolean
) {
  switch (true) {
    // plan added
    case isSubscriptionInBasket:
      return t(
        'productDetailsHero.drawers.subscriptionOptionsDrawer.planAdded'
      );
    case hasSubscription:
      // update plan
      return t(
        'productDetailsHero.drawers.subscriptionOptionsDrawer.updatePlan'
      );
    default:
      // add plan
      return t(
        'productDetailsHero.drawers.subscriptionOptionsDrawer.saveSubscription'
      );
  }
}
